import { useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { useLocation } from 'react-router-dom';

export type InputParameters = {
  appInstanceId?: string;
  experimentId?: string;
  locale?: string;
  interstitial?: string;
  productCategory?: string;
  projectId?: string;
  resume?: string;
  status?: string;
  photoIds?: string[];
};

const arrayInputParmeterKeys = ['photoIds'];

const inputKeys = {
  appInstanceIdKey: 'appInstanceId',
  experimentIdKey: 'experimentId',
  localeKey: 'locale',
  interstitialKey: 'interstitial',
  productCategoryKey: 'productCategory',
  projectIdKey: 'projectId',
  resumeKey: 'resume',
  status: 'status',
  photoIds: 'photoIds',
};

export const setUrlParams = (paramsObject: Record<string, string[] | string | null>): string | null => {
  Object.keys(paramsObject).forEach((key) => {
    if (paramsObject[key] == null) delete paramsObject[key];
  });

  const paramsString = new URLSearchParams(paramsObject as Record<string, string>).toString();
  return paramsString !== '' ? `?${paramsString}` : null;
};

// Get fields from both query and hash search params
export const getUrlParams = (
  searchA: URLSearchParams,
  searchB?: URLSearchParams | null,
  paramsKeys: string[] = Object.values(inputKeys),
): Record<string, string | string[]> => {
  return paramsKeys.reduce(
    (params, key) => ({
      ...params,
      [key]: getStringOrArrayFrom(searchA, key) || (searchB ? getStringOrArrayFrom(searchB, key) : null),
    }),
    {},
  );
};

const getStringOrArrayFrom = (params: URLSearchParams, key: string): string[] | string | null => {
  if (arrayInputParmeterKeys.includes(key)) return params.getAll(key);
  return params.get(key);
};

export const useInputParameters = (): InputParameters => {
  const router = { location: useLocation() };
  const [cookies, setCookie] = useCookies([inputKeys.appInstanceIdKey, inputKeys.experimentIdKey, inputKeys.localeKey]);

  const urlParams = useMemo(() => {
    return getUrlParams(
      new URLSearchParams(window.location.search),
      new URLSearchParams(router.location.search),
      Object.values(inputKeys),
    );
  }, [router.location.search]);

  const inputs: InputParameters = urlParams;

  if (inputs.appInstanceId == null) {
    inputs.appInstanceId = cookies.appInstanceId;
  } else {
    setCookie(inputKeys.appInstanceIdKey, inputs.appInstanceId);
  }

  if (inputs.experimentId == null) {
    inputs.experimentId = cookies.experimentId;
  } else {
    setCookie(inputKeys.experimentIdKey, inputs.experimentId);
  }

  if (inputs.locale == null) {
    inputs.locale = cookies.locale;
  } else {
    setCookie(inputKeys.localeKey, inputs.locale);
  }

  return inputs;
};
